import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { calculatorDefinitions, searchCalculators } from '../calculators/registry';
import type { CalculatorCategory } from '../calculators/types';
import { CALCULATORS_SEO } from '../seo/site';
import { useSeo } from '../seo/seo';
import { buildCalculatorDirectoryStructuredData } from '../seo/structuredData';

const categoryOrder: readonly CalculatorCategory[] = ['pricing', 'business', 'tax', 'advertising'];

const categoryLabels: Record<CalculatorCategory, string> = {
  pricing: '가격',
  business: '사업',
  tax: '세금 기초',
  advertising: '광고',
};

export function HomePage() {
  const [query, setQuery] = useState('');
  const searchResults = searchCalculators(query);
  const structuredData = useMemo(() => buildCalculatorDirectoryStructuredData(), []);

  useSeo(CALCULATORS_SEO, structuredData);

  return (
    <>
      <section className="hero" aria-labelledby="home-title">
        <p className="eyebrow">BUSINESS CALCULATORS</p>
        <h1 id="home-title">사업 계산기 10종.</h1>
        <p className="hero-description">
          가격, 마진, 손익, 광고비 의사결정에 필요한 계산 결과를 몇 초 안에 확인합니다.
        </p>
        <label className="search-field">
          <span className="sr-only">계산기 검색</span>
          <input
            type="search"
            placeholder="마진, 부가세, ROAS 검색"
            aria-label="계산기 검색"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <p className="search-note">현재 핵심 계산기 {calculatorDefinitions.length}개를 바로 사용할 수 있습니다.</p>
      </section>

      {query.trim() ? (
        <section className="search-results" aria-labelledby="search-results-title">
          <h2 id="search-results-title">검색 결과</h2>
          {searchResults.length > 0 ? (
            <div className="tool-list">
              {searchResults.map((calculator) => (
                <Link className="tool-link" key={calculator.slug} to={`/calculators/${calculator.slug}`}>
                  <strong>{calculator.title}</strong>
                  <span>{calculator.description}</span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="empty-search">검색어와 일치하는 계산기가 없습니다.</p>
          )}
        </section>
      ) : null}

      <section aria-labelledby="calculator-categories">
        <h2 id="calculator-categories">계산기 영역</h2>
        <div className="category-grid">
          {categoryOrder.map((category) => {
            const calculators = calculatorDefinitions.filter((calculator) => calculator.category === category);
            return (
              <article className="category" key={category}>
                <h3>{categoryLabels[category]}</h3>
                <div className="category-tools">
                  {calculators.map((calculator) => (
                    <Link key={calculator.slug} to={`/calculators/${calculator.slug}`}>
                      <strong>{calculator.title}</strong>
                      <span>{calculator.description}</span>
                    </Link>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}
